import { createRouter, createRoute, createRootRoute, redirect } from '@tanstack/react-router'

import Login from './pages/login'
import Callback from './pages/callback'
import Dashboard from './pages/dashboard'
import DNSManagement from './pages/dns'
import CertificateManagement from './pages/certificates'
import AuthTokens from './pages/auth'

const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
const TOKEN_KEY = 'aip_dns_access_token'

function isAuthenticated(): boolean {
  return SKIP_AUTH || localStorage.getItem(TOKEN_KEY) !== null
}

const rootRoute = createRootRoute()

// ─── Index redirect ───────────────────────────────────────────────────────────

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' })
    } else {
      throw redirect({ to: '/login' })
    }
  },
  component: () => null,
})

// ─── Public routes ────────────────────────────────────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
})

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/callback',
  component: Callback,
})

// ─── Protected route guard ────────────────────────────────────────────────────

function requireAuth() {
  if (!isAuthenticated()) {
    throw redirect({ to: '/login' })
  }
}

// ─── Protected routes ─────────────────────────────────────────────────────────

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: requireAuth,
  component: Dashboard,
})

const dnsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dns',
  beforeLoad: requireAuth,
  component: DNSManagement,
})

const certificatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/certificates',
  beforeLoad: requireAuth,
  component: CertificateManagement,
})

// Public on purpose: it doubles as a token-minting / diagnostics tool
// (device flow, decoded-claims inspector) that must work even when the
// current token is rejected or absent.
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthTokens,
})

// ─── Route tree ───────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  callbackRoute,
  dashboardRoute,
  dnsRoute,
  certificatesRoute,
  authRoute,
])

export const router = createRouter({
  routeTree,
  basepath: '/ui',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
